import { formatNumber } from "@/helpers/common/formatNumber";
import { convertLetters } from "@/utils/letterConversion";
import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment } from "react";

interface PlayerTag {
    id: string;
    tagTier: {
        tag: string;
        tier: number;
        tagName: string;
        shieldValue: number;
        subtitle: string;
        unitName: string;
    };
    createdAt: string;
    updatedAt: string;
    imageUrl: string;
}

interface AllShieldTag {
    name: string;
    slug: string;
    milestones: number[];
    maxTiers: number;
    shieldValues: number[];
    imageTemplate: string;
    subtitle: string;
    unitName: string;
}

interface TagInfoModalProps {
    isOpen: boolean;
    onClose: () => void;
    tag: AllShieldTag;
    playerTag?: PlayerTag;
}

const TagInfoModal: React.FC<TagInfoModalProps> = ({
    isOpen,
    onClose,
    tag,
    playerTag,
}) => {
    const currentTier = playerTag?.tagTier.tier ?? 0;
    const nextMilestone = playerTag
        ? currentTier < tag.maxTiers
            ? tag.milestones[currentTier]
            : "Max Tier Reached"
        : tag.milestones[0];
    const currentShieldValue = playerTag?.tagTier.shieldValue ?? null;

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[999]" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="bg-brand_charcoal/80 fixed inset-0" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="border-brand bg-brand_charcoal w-full max-w-sm transform overflow-hidden rounded-lg border-4 p-6 text-left align-middle shadow-xl transition-all">
                                <Dialog.Title
                                    as="h3"
                                    className="font-prviking text-brand mb-1 text-center text-2xl font-bold"
                                >
                                    {convertLetters(tag.name)}
                                </Dialog.Title>
                                <p className="text-stats mb-4 text-center font-semibold">
                                    {tag.subtitle}
                                </p>
                                <div className="text-brand space-y-2 text-sm">
                                    {playerTag ? (
                                        <>
                                            <p>
                                                <strong>CURRENT TIER:</strong>{" "}
                                                {formatNumber(currentTier)}
                                            </p>
                                            <p>
                                                <strong>SHIELD VALUE:</strong>{" "}
                                                {formatNumber(
                                                    currentShieldValue,
                                                )}
                                            </p>
                                            {nextMilestone ===
                                            "Max Tier Reached" ? (
                                                <p>
                                                    <strong>
                                                        MAX TIER REACHED
                                                    </strong>
                                                </p>
                                            ) : (
                                                <p>
                                                    <strong>NEXT TIER:</strong>{" "}
                                                    {formatNumber(
                                                        nextMilestone,
                                                    )}{" "}
                                                    {tag.unitName}
                                                </p>
                                            )}
                                        </>
                                    ) : (
                                        <p>
                                            <strong>NEXT TIER:</strong>{" "}
                                            {formatNumber(nextMilestone)}{" "}
                                            {tag.unitName}
                                        </p>
                                    )}
                                </div>
                                <div className="mt-6 flex justify-end">
                                    <button
                                        className="text-brand border-brand rounded border-2 bg-blue-600 px-4 py-2 hover:bg-blue-700"
                                        onClick={onClose}
                                    >
                                        Close
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default TagInfoModal;
